import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { setLoadingAndError } from "@datorama/akita";
import * as Sentry from "@sentry/angular";
import LogRocket from "logrocket";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ApiService } from "../api/api.service";
import { FlagsmithService } from "../flagsmith/services/flagsmith.service";
import { NavigationService } from "../navigation/navigation.service";
import { AppStore } from "./app.store";

@Injectable({
    providedIn: "root"
})
export class AppService {
    constructor(
        private store: AppStore,
        private api: ApiService,
        private navigationService: NavigationService,
        private flagsmithService: FlagsmithService,
        private router: Router
    ) {}

    public init(): Observable<void> {
        return this.api.organisation.getInfo().pipe(
            map(organisation => this.store.update({ organisation })),
            catchError(() => {
                this.router.navigate(["/unauthorized"]);
                return EMPTY;
            })
        );
    }

    public loadServerState(): Observable<void> {
        return this.api.info.getInfo().pipe(
            setLoadingAndError(this.store),
            map(serverState => this.store.update({ serverState }))
        );
    }

    public logout(): Observable<void> {
        return this.api.session.logout().pipe(
            map(() => {
                this.store.update({ authError: true, authorized: false, currentAccount: null });
                Sentry.setUser(null);
                LogRocket.identify({});
            })
        );
    }

    public loadCurrentAccount(): Observable<void> {
        const { currentAccount } = this.store.getValue();
        if (currentAccount) {
            return of(void 0);
        }

        return this.api.account.getInfo().pipe(
            map(account => {
                this.store.update({ currentAccount: account, authorized: true });

                if (!account.userId) {
                    return;
                }

                Sentry.setUser({
                    id: account.userId,
                    email: account.email,
                    username: account.fullName
                });
                LogRocket.identify(account.email ?? account.userId, {
                    id: account.userId,
                    role: account.role
                });

                this.flagsmithService.identify(account.userId, {});
            }),
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Forbidden) {
                    this.router.navigate(["/unauthorized"]);
                }

                if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
                    this.router.navigate(["/"]);
                }

                this.store.update({ authorized: false });

                return EMPTY;
            })
        );
    }
}
